import React, { useState } from 'react';
import { useStore } from '../context/StoreContext.tsx';
import { ArrowRight, Sparkles, Smile, X, LayoutTemplate } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Onboarding: React.FC = () => {
  const { userProfile, completeOnboarding } = useStore();
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  // If loading or already completed, do not show
  if (!userProfile || userProfile.onboardingCompleted) return null;

  const handleFinish = () => {
    completeOnboarding();
    navigate('/generate');
  };

  const steps = [
    {
      title: "Welcome to Chip AI Studio 👋",
      content: (
        <div className="space-y-4">
          <div className="flex justify-center my-6">
            <div className="w-32 h-32 bg-steelhead/10 rounded-full flex items-center justify-center text-4xl animate-bounce">
              👋
            </div>
          </div>
          <p className="text-lg text-gray-600">
            Your new home for creating <strong>on-brand Chip content</strong> in seconds—no prompt engineering required.
          </p>
          <ul className="text-sm text-gray-500 space-y-2 bg-gray-50 p-4 rounded-lg">
            <li className="flex items-center gap-2">✓ Meet Chip (officially)</li>
            <li className="flex items-center gap-2">✓ Learn how the studio works</li>
            <li className="flex items-center gap-2">✓ Create your first Chip image</li>
          </ul>
        </div>
      ),
      action: "Let's Go!",
      onAction: () => setStep(1)
    },
    {
      title: "Meet Chip",
      content: (
        <div className="space-y-4">
           <div className="flex justify-center my-6">
             <div className="relative">
                <div className="w-32 h-32 bg-white border-4 border-steelhead rounded-full flex items-center justify-center overflow-hidden shadow-lg">
                   {/* Placeholder for canonical chip */}
                   <Smile size={64} className="text-gray-800" />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-happy text-steelhead-dark px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                  Est. 1996
                </div>
             </div>
           </div>
           <p className="text-gray-600">
             Chip embodies <strong>"Exhibit Happy."</strong> He's friendly, clever, and brings joy to the trade show world.
           </p>
           <div className="grid grid-cols-2 gap-3 text-sm">
             <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
               <strong>Visuals</strong><br/>
               Matte white, smooth, minimal features.
             </div>
             <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
               <strong>Voice</strong><br/>
               Warm, helpful, never cheesy.
             </div>
           </div>
        </div>
      ),
      action: "Next",
      onAction: () => setStep(2)
    },
    {
      title: "How It Works",
      content: (
        <div className="space-y-6">
           <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">1</div>
                <div>
                   <h4 className="font-bold text-gray-900">Pick a Scenario</h4>
                   <p className="text-xs text-gray-500">What is Chip doing? (Greeting, Working, etc.)</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">2</div>
                <div>
                   <h4 className="font-bold text-gray-900">Choose Format</h4>
                   <p className="text-xs text-gray-500">Square for Insta, Portrait for Stories.</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-happy/50 text-steelhead-dark flex items-center justify-center font-bold">3</div>
                <div>
                   <h4 className="font-bold text-gray-900">Hit Generate</h4>
                   <p className="text-xs text-gray-500">AI handles the prompts and DNA.</p>
                </div>
              </div>
           </div>
        </div>
      ),
      action: "Let's Try It",
      onAction: handleFinish,
      icon: <Sparkles size={18} />
    }
  ];

  const currentStep = steps[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[scale_0.2s_ease-out]">
        <button 
          onClick={completeOnboarding}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1"
          title="Skip Intro"
        >
          <X size={20} />
        </button>

        <div className="p-8 text-center">
           <h2 className="text-2xl font-bold text-steelhead mb-2">{currentStep.title}</h2>
           
           <div className="min-h-[280px] flex flex-col justify-center text-left">
             {currentStep.content}
           </div>

           <div className="mt-8 flex flex-col gap-3">
             <button 
               onClick={currentStep.onAction}
               className="w-full py-3.5 bg-steelhead hover:bg-steelhead-light text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
             >
               {currentStep.icon}
               {currentStep.action}
               {!currentStep.icon && <ArrowRight size={20} />}
             </button>
             
             <div className="flex justify-center gap-1.5 mt-2">
               {steps.map((_, i) => (
                 <div 
                   key={i} 
                   className={`h-1.5 rounded-full transition-all ${i === step ? 'w-8 bg-steelhead' : 'w-2 bg-gray-200'}`} 
                 />
               ))}
             </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;